<template>
  <div class="car-catalog-page">
    <section class="container px-4 md:px-6 lg:px-8 pt-2 lg:pt-8 pb-16">
      <div class="grid grid-cols-full lg:grid-cols-12 lg:gap-10 gap-4">
        <aside v-if="!hideVehicleCatalogFilters" class="lg:col-span-3">
          <h2 class="font-lg font-medium min-h-6">
            <span v-show="!isLoading">
              {{ carList.length }} {{ $t('vehicle', carList.length) }} {{ $t('are_awaiting', carList.length) }}
            </span>
          </h2>

          <hr class="my-4 h-px bg-black border-0">

          <CatalogFilters
            ref="$catalogFilters"
            :loading="isLoading"
            @fetch="fetchVehicles"
          />
        </aside>

        <div :class="hideVehicleCatalogFilters ? 'lg:col-span-full': 'lg:col-span-9'">
          <header v-if="banner" class="mt-4 xl:mb-16 mb-6">
            <BaseBannerSimple
              :title="(banner?.title as string)"
              :title-level="banner.level"
              :subtitle="(banner.subtitle as string)"
              :heading-title="banner.heading_title"
              :heading-sub-title="banner.heading_subtitle"
              :heading-level="banner.heading_level"
              :image="(banner.image_desktop as WagtailImage)"
              :variant="banner.variant"
              :cta-link="banner.cta_button_link"
              :cta-text="banner.cta_button_text"
            />
          </header>

          <template v-if="!hideVehicleCatalogFilters">
            <CatalogFiltersPills v-if="false" class="my-4" />
          </template>

          <div class="items-grid" :class="hideVehicleCatalogFilters ? 'xl:grid-cols-4 lg:grid-cols-3 sm:grid-cols-2' : 'xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-2'">
            <template v-if="!isLoading">
              <VehicleCard
                v-for="car in (carList as CarCardType[])"
                :key="car.id"
                v-bind="car"
                data-aos="fade-in"
                @click="onClickCarCard(car.id as string)"
              />
            </template>
            <template v-else>
              <div
                v-for="i in 8"
                :key="i"
                role="status"
                class="animate-pulse my-4 w-full"
              >
                <div class="h-32 bg-brand-black/20 rounded-sm w-full mb-4" />
                <div class="h-8 bg-brand-black/20 rounded-sm w-3/4 mb-2.5 mx-auto" />
                <div class="h-8 bg-brand-black/20 rounded-sm w-full" />
                <span class="sr-only">Loading...</span>
              </div>
            </template>
          </div>

          <Transition
            enter-active-class="duration-700 ease-out"
            enter-from-class="transform opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="duration-0 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="transform opacity-0"
          >
            <CatalogEmptyStateForm
              v-if="!carList.length && !isLoading"
              class="mt-16"
              @retry="onRetry"
            />
          </Transition>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, shallowRef, inject, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useSubscriptionCatalogStore } from '~/stores/subscriptionCatalogStore';
import { useApi, useFeatureHandler, useGTM } from '~/composables';
import type { BannerResponse, CarCard as CarCardType, EcosystemVehicle, WagtailImage } from '~/types';
import { apiVehicleToCarCardType } from '~/lib/transformers';
import CatalogEmptyStateForm from '~/components/forms/CatalogEmptyStateForm.vue';
import { useI18n, useServerSeoMeta, useHead, useRoute, useSeoMeta } from '#imports';
import { TRACKING_EVENTS } from '~/lib/constants';
import BaseBannerSimple from '~/components/BaseBannerSimple.vue';
import CatalogFilters, { type ExposedProps as CatalogFilterExposedProps } from '~/components/catalog-filters/CatalogFilters.vue';
import CatalogFiltersPills from '~/components/catalog-filters/CatalogFiltersPills.vue';
import VehicleCard from '~/components/cards/VehicleCard.vue';
import { serializeQueryParams } from '~/lib/utils';

const { locale } = useI18n();
const { hideVehicleCatalogFilters } = useFeatureHandler();
const webSettings = inject('settings') as Record<string, any>;
const banner = computed<BannerResponse>(() => {
  return webSettings.banners
    .find((b: BannerResponse) => b?.code === 'car_catalog_banner' && (b.locale === locale.value));
});

useHead({
  title: 'Catálogo de coches',
});

useServerSeoMeta({
  title: 'Catálogo de coches',
  ogTitle: 'Catálogo de coches',
  description: 'Entra y echa un vistazo a nuestro servicio de suscripción adaptado para las necesidades de tu negocio.',
  ogDescription: 'Entra y echa un vistazo a nuestro servicio de suscripción adaptado para las necesidades de tu negocio.',
  // ogImage: 'https://example.com/image.png',
});

useSeoMeta({
  title: 'Catálogo de coches',
  ogTitle: 'Catálogo de coches',
  description: 'Entra y echa un vistazo a nuestro servicio de suscripción adaptado para las necesidades de tu negocio.',
  ogDescription: 'Entra y echa un vistazo a nuestro servicio de suscripción adaptado para las necesidades de tu negocio.',
  // ogImage: 'https://example.com/image.png',
});

// eslint-disable-next-line no-undef
definePageMeta({
  layout: 'default-no-footer',
});

const route = useRoute();
const { getVehicles, getAvailableSubscriptionMonths } = useApi();
const catalogStore = useSubscriptionCatalogStore();
const { pagination, months } = storeToRefs(catalogStore);
const isLoading = ref(true);
const $catalogFilters = ref<CatalogFilterExposedProps | null>(null);
const carList = shallowRef<CarCardType[]>([]);

if (hideVehicleCatalogFilters) {
  let qParams = {};
  const response = await getAvailableSubscriptionMonths();
  const results = response.data?.results;
  if (results.length > 0) {
    const result = response.data?.results
      .toSorted((a: number, b: number) => b - a)
      .map((month: number) => (String(month))) || [];

    qParams = {
      months: result.at(0),
      order_by: 'price',
    };

    months.value = result.at(0);
  }

  fetchVehicles(serializeQueryParams(qParams));
}

async function fetchVehicles (queryParams: string) {
  try {
    isLoading.value = true;
    const response = await getVehicles(queryParams);
    const sortedVehicles = sortByPriceAsc(response.data?.results);
    carList.value = apiVehicleToCarCardType(sortedVehicles, Number(months.value), locale.value as 'es' | 'en');
    // Update pagination after each fetch
    pagination.value.currentPage = 1;

    return carList;
  } catch (error: any) {
    // eslint-disable-next-line no-console
    console.log('[catalog] error: ', error);
    const response = error?.response || null;
    if (response?.status === 500) {
      // eslint-disable-next-line no-undef
      throw showError({
        statusCode: response?.status,
        statusMessage: response.message,
        stack: response.data,
      });
    }
  } finally {
    isLoading.value = false;
  }
}

function sortByPriceAsc (vehicles: EcosystemVehicle[]) {
  const getHighestPrice = (vehicle: EcosystemVehicle) => Math.max(
    ...vehicle.prices.map(p => p.price.value)
  );
  const byPriceAsc = (a: EcosystemVehicle, b: EcosystemVehicle) => getHighestPrice(a) - getHighestPrice(b);

  return vehicles.toSorted(byPriceAsc);
}

function onRetry () {
  isLoading.value = true;
  setTimeout(async () => {
    if (hideVehicleCatalogFilters) {
      fetchVehicles('');
    } else {
      $catalogFilters.value && $catalogFilters.value.init(route.params);
    }
  }, 800);
}

const { triggerEvent } = useGTM();
function onClickCarCard (vehicleId: string) {
  triggerEvent(TRACKING_EVENTS.CLICK_VEHICLE_LIST, {
    vehicleId,
    months: months.value,
  });
}
</script>

<style scoped>
.items-grid {
  @apply grid gap-3;
}
</style>
